import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Lenis from 'lenis';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

let lenisInstance = null;

/**
 * Buttery momentum scrolling via Lenis, synced with GSAP ScrollTrigger.
 * Honors prefers-reduced-motion by skipping the smooth layer.
 */
const SmoothScroll = ({ children }) => {
  const location = useLocation();

  useEffect(() => {
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) return;

    const lenis = new Lenis({
      duration: 1.15,
      easing: t => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      touchMultiplier: 1.6,
    });
    lenisInstance = lenis;

    lenis.on('scroll', ScrollTrigger.update);

    const raf = time => {
      lenis.raf(time * 1000);
    };
    gsap.ticker.add(raf);
    gsap.ticker.lagSmoothing(0);

    return () => {
      gsap.ticker.remove(raf);
      lenis.destroy();
      lenisInstance = null;
    };
  }, []);

  // Reset scroll on route change. The new page may be far shorter than the one
  // we left (e.g. the very tall cinematic Home), so we jump to the top and then
  // recalculate Lenis dimensions + ScrollTrigger once the new page has settled,
  // otherwise scrolling can appear "stuck".
  useEffect(() => {
    if (lenisInstance) {
      lenisInstance.scrollTo(0, { immediate: true });
    } else {
      window.scrollTo(0, 0);
    }

    const raf = requestAnimationFrame(() => {
      lenisInstance?.resize?.();
      ScrollTrigger.refresh();
    });
    // Second pass after the page-transition (framer-motion) has finished.
    const timer = setTimeout(() => {
      lenisInstance?.resize?.();
      ScrollTrigger.refresh();
    }, 500);

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timer);
    };
  }, [location.pathname]);

  return children;
};

export default SmoothScroll;
